/**
 * SERVICIO DE DISTRIBUIDORES CON SUPABASE
 * 
 * Maneja operaciones de distribuidores incluyendo gestión de licencias
 * y asignación a empresas cliente.
 */

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

// Cambiar a false cuando el backend esté listo
const useMock = true;

// ========================================
// DATOS MOCK PARA DESARROLLO
// ========================================

const mockPlanes = [
  {
    id: "plan_bronze",
    nombre: "Plan Bronze",
    cantidad_tests: 100,
    precio: 150000,
    precio_unitario: 1500,
    descripcion: "Ideal para distribuidores que inician con volúmenes pequeños de tests.",
    caracteristicas: [
      "Soporte técnico básico",
      "Reportes mensuales",
      "Acceso a materiales promocionales",
    ],
  },
  {
    id: "plan_silver",
    nombre: "Plan Silver",
    cantidad_tests: 500,
    precio: 650000,
    precio_unitario: 1300,
    descripcion: "Perfecto para distribuidores con demanda media y crecimiento constante.",
    caracteristicas: [
      "Soporte técnico prioritario",
      "Reportes quincenales",
      "Kit promocional completo",
      "Descuento del 13%",
    ],
  },
  {
    id: "plan_gold",
    nombre: "Plan Gold",
    cantidad_tests: 1000,
    precio: 1100000,
    precio_unitario: 1100,
    descripcion: "La mejor opción para grandes volúmenes con máximos beneficios.",
    caracteristicas: [
      "Soporte técnico 24/7",
      "Reportes semanales",
      "Marketing personalizado",
      "Descuento del 27%",
      "Capacitación exclusiva",
    ],
  },
];

// Mock de licencias del distribuidor
const mockLicenciasDistribuidor = {
  total: 1000,
  asignadas: 350,
  disponibles: 650,
  usadas: 200,
  limiteMaximo: "Sin límites"
};

// ========================================
// HELPER FUNCTIONS
// ========================================

/**
 * Obtiene el token de autenticación desde localStorage
 */
const getAuthToken = () => {
  return localStorage.getItem('access_token');
};

/**
 * Obtiene los headers necesarios para las peticiones autenticadas
 */
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    'apikey': SUPABASE_ANON_KEY,
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Obtiene el ID del distribuidor del usuario actual
 */
const getCurrentDistribuidorId = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;

    const user = JSON.parse(userStr);
    
    // Para modo simulación
    if (user.id === 'mock-distribuidor-001') {
      return 'mock-distribuidor-001';
    }
    
    return user.companyData?.id_distribuidor || user.distribuidorData?.id_distribuidor || null;
  } catch (error) {
    console.error('Error al obtener ID de distribuidor:', error);
    return null;
  }
};

// ========================================
// SERVICIO PRINCIPAL
// ========================================

const distributorService = {
  
  // ========================================
  // GESTIÓN DE PLANES
  // ========================================
  
  /**
   * Obtiene todos los planes disponibles
   */
  getPlanes: async () => {
    if (useMock) {
      return new Promise((resolve) =>
        setTimeout(() => resolve(mockPlanes), 400)
      );
    }

    try {
      const response = await fetch(`${API_BASE_URL}/distributor-plans`);
      if (!response.ok) {
        console.warn("API no disponible, usando datos de prueba");
        return mockPlanes;
      }
      return await response.json();
    } catch (error) {
      console.error("Error in getPlanes:", error);
      return mockPlanes;
    }
  },

  /**
   * Obtiene un plan específico por ID
   */
  getPlanById: async (planId) => {
    if (useMock) {
      return new Promise((resolve) =>
        setTimeout(
          () => resolve(mockPlanes.find((p) => p.id === planId)),
          300
        )
      );
    }

    try {
      const response = await fetch(`${API_BASE_URL}/distributor-plans/${planId}`);
      if (!response.ok) {
        const plan = mockPlanes.find((p) => p.id === planId);
        if (!plan) throw new Error("Plan no encontrado");
        return plan;
      }
      return await response.json();
    } catch (error) {
      console.error("Error in getPlanById:", error);
      const plan = mockPlanes.find((p) => p.id === planId);
      if (!plan) throw new Error("Plan no encontrado");
      return plan;
    }
  },

  /**
   * Procesa la compra de un plan
   */
  procesarCompraPlan: async (empresaId, planId) => {
    if (useMock) {
      return new Promise((resolve) =>
        setTimeout(
          () =>
            resolve({
              success: true,
              transactionId: `txn_${Date.now()}`,
              message: "Plan adquirido exitosamente (simulado)",
            }),
          500
        )
      );
    }

    try {
      const response = await fetch(`${API_BASE_URL}/distributor-plans/purchase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          empresaId,
          planId,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        console.warn("API no disponible, simulando compra exitosa");
        return {
          success: true,
          transactionId: `txn_${Date.now()}`,
          message: "Plan adquirido exitosamente (simulado)",
        };
      }

      return await response.json();
    } catch (error) {
      console.error("Error in procesarCompraPlan:", error);
      return {
        success: true,
        transactionId: `txn_${Date.now()}`,
        message: "Plan adquirido exitosamente (simulado)",
      };
    }
  },

  // ========================================
  // GESTIÓN DE LICENCIAS - NUEVO
  // ========================================

  /**
   * Obtiene información de licencias del distribuidor actual
   * @returns {Promise<Object>} Información de licencias
   */
  getLicenciasDistribuidor: async () => {
    const distribuidorId = getCurrentDistribuidorId();
    
    if (!distribuidorId) {
      throw new Error('No se encontró ID de distribuidor');
    }

    // Modo simulación
    if (distribuidorId === 'mock-distribuidor-001' || useMock) {
      console.log('📊 Usando datos mock de licencias distribuidor');
      return new Promise((resolve) =>
        setTimeout(() => resolve(mockLicenciasDistribuidor), 400)
      );
    }

    try {
      // Consultar el distribuidor desde Supabase
      const url = `${SUPABASE_URL}/rest/v1/distribuidor_autorizado?id_distribuidor=eq.${distribuidorId}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Error al obtener información del distribuidor');
      }

      const data = await response.json();
      
      if (!data || data.length === 0) {
        throw new Error('Distribuidor no encontrado');
      }

      const distribuidor = data[0];
      
      // Calcular licencias disponibles
      const licenciasCompradas = distribuidor.licencias_compradas || 0;
      const licenciasVendidas = distribuidor.licencias_vendidas || 0;
      const licenciasDisponibles = licenciasCompradas - licenciasVendidas;

      return {
        total: licenciasCompradas,
        asignadas: licenciasVendidas,
        disponibles: licenciasDisponibles,
        usadas: 0, // Este dato vendría de otra tabla si es necesario
        limiteMaximo: "Sin límites"
      };

    } catch (error) {
      console.error('Error al obtener licencias del distribuidor:', error);
      throw error;
    }
  },

  /**
   * Obtiene todas las empresas asociadas al distribuidor actual
   * @returns {Promise<Array>} Lista de empresas
   */
  getEmpresasDistribuidor: async () => {
    const distribuidorId = getCurrentDistribuidorId();
    
    if (!distribuidorId) {
      throw new Error('No se encontró ID de distribuidor');
    }

    // Modo simulación - datos mock
    if (distribuidorId === 'mock-distribuidor-001' || useMock) {
      console.log('📊 Usando datos mock de empresas');
      return new Promise((resolve) =>
        setTimeout(() => resolve([
          {
            id_empresa: "1",
            nombre_empresa: "Empresa Demo XYZ",
            nit: "900123456",
            email: "contacto@xyz.com",
            telefono: "3001234567",
            direccion: "Calle 123 #45-67",
            ciudad: "Bogotá",
            persona_contacto: "María García",
            cargo: "Gerente RRHH",
            licencias_compradas: 50,
            licencias_usadas: 10,
            descripcion: "Cliente activo desde 2024"
          },
          {
            id_empresa: "2",
            nombre_empresa: "Corporación ABC S.A.S",
            nit: "900654321",
            email: "info@abc.com",
            telefono: "3107654321",
            direccion: "Carrera 45 #12-34",
            ciudad: "Medellín",
            persona_contacto: "Juan Pérez",
            cargo: "Director Talento",
            licencias_compradas: 0,
            licencias_usadas: 0,
            descripcion: "Nueva empresa registrada"
          },
          {
            id_empresa: "3",
            nombre_empresa: "Tecnología Digital Ltda",
            nit: "900789456",
            email: "contacto@tecdigital.com",
            telefono: "3159876543",
            direccion: "Avenida 68 #89-12",
            ciudad: "Cali",
            persona_contacto: "Ana Martínez",
            cargo: "Coordinadora RH",
            licencias_compradas: 100,
            licencias_usadas: 45,
            descripcion: "Cliente preferencial"
          }
        ]), 500)
      );
    }

    try {
      const url = `${SUPABASE_URL}/rest/v1/company?id_distribuidor=eq.${distribuidorId}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Error al obtener empresas del distribuidor');
      }

      const data = await response.json();
      return data;

    } catch (error) {
      console.error('Error al obtener empresas del distribuidor:', error);
      throw error;
    }
  },

  /**
   * Asigna licencias a múltiples empresas
   * @param {Array} asignaciones - Array de objetos {id_empresa, cantidad_licencias}
   * @returns {Promise<Object>} Resultado de la operación
   */
  asignarLicenciasEmpresas: async (asignaciones) => {
    const distribuidorId = getCurrentDistribuidorId();
    
    if (!distribuidorId) {
      throw new Error('No se encontró ID de distribuidor');
    }

    // Modo simulación
    if (distribuidorId === 'mock-distribuidor-001' || useMock) {
      console.log('✅ Simulando asignación de licencias:', asignaciones);
      return new Promise((resolve) =>
        setTimeout(() => resolve({
          success: true,
          message: `Licencias asignadas exitosamente a ${asignaciones.length} empresa(s)`,
          asignaciones: asignaciones
        }), 1000)
      );
    }

    try {
      // Calcular total de licencias a asignar
      const totalLicencias = asignaciones.reduce((sum, asig) => sum + asig.cantidad_licencias, 0);

      // Validar que el distribuidor tenga licencias suficientes
      const licenciasInfo = await distributorService.getLicenciasDistribuidor();
      
      if (totalLicencias > licenciasInfo.disponibles) {
        throw new Error(`No hay suficientes licencias disponibles. Solicitadas: ${totalLicencias}, Disponibles: ${licenciasInfo.disponibles}`);
      }

      // Actualizar cada empresa con las licencias asignadas
      const resultados = [];
      
      for (const asignacion of asignaciones) {
        try {
          // Obtener datos actuales de la empresa
          const empresaUrl = `${SUPABASE_URL}/rest/v1/company?id_empresa=eq.${asignacion.id_empresa}`;
          const empresaResponse = await fetch(empresaUrl, {
            method: 'GET',
            headers: getAuthHeaders()
          });

          if (!empresaResponse.ok) {
            throw new Error(`Error al obtener empresa ${asignacion.id_empresa}`);
          }

          const empresaData = await empresaResponse.json();
          
          if (!empresaData || empresaData.length === 0) {
            throw new Error(`Empresa ${asignacion.id_empresa} no encontrada`);
          }

          const empresa = empresaData[0];

          // Calcular nuevas licencias
          const licenciasCompradasActuales = empresa.licencias_compradas || 0;
          const nuevasLicenciasCompradas = licenciasCompradasActuales + asignacion.cantidad_licencias;

          // Actualizar empresa
          const headers = getAuthHeaders();
          headers['Prefer'] = 'return=representation';

          const updateUrl = `${SUPABASE_URL}/rest/v1/company?id_empresa=eq.${asignacion.id_empresa}`;
          const updateResponse = await fetch(updateUrl, {
            method: 'PATCH',
            headers: headers,
            body: JSON.stringify({
              licencias_compradas: nuevasLicenciasCompradas
            })
          });

          if (!updateResponse.ok) {
            throw new Error(`Error al actualizar empresa ${asignacion.id_empresa}`);
          }

          resultados.push({
            id_empresa: asignacion.id_empresa,
            success: true,
            licencias_asignadas: asignacion.cantidad_licencias
          });

        } catch (error) {
          resultados.push({
            id_empresa: asignacion.id_empresa,
            success: false,
            error: error.message
          });
        }
      }

      // Actualizar contador de licencias vendidas del distribuidor
      const distribuidorUrl = `${SUPABASE_URL}/rest/v1/distribuidor_autorizado?id_distribuidor=eq.${distribuidorId}`;
      const distribuidorResponse = await fetch(distribuidorUrl, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (distribuidorResponse.ok) {
        const distribuidorData = await distribuidorResponse.json();
        
        if (distribuidorData && distribuidorData.length > 0) {
          const distribuidor = distribuidorData[0];
          const licenciasVendidasActuales = distribuidor.licencias_vendidas || 0;
          const nuevasLicenciasVendidas = licenciasVendidasActuales + totalLicencias;

          const headers = getAuthHeaders();
          headers['Prefer'] = 'return=representation';

          await fetch(distribuidorUrl, {
            method: 'PATCH',
            headers: headers,
            body: JSON.stringify({
              licencias_vendidas: nuevasLicenciasVendidas
            })
          });
        }
      }

      // Verificar si todas fueron exitosas
      const todasExitosas = resultados.every(r => r.success);

      return {
        success: todasExitosas,
        message: todasExitosas 
          ? `Licencias asignadas exitosamente a ${asignaciones.length} empresa(s)`
          : 'Algunas asignaciones fallaron',
        resultados: resultados
      };

    } catch (error) {
      console.error('Error al asignar licencias:', error);
      throw error;
    }
  },

  // ========================================
  // CRUD DE DISTRIBUIDORES
  // ========================================

  getAllDistributors: async () => {
    const response = await fetch(`${API_BASE_URL}/distributors`);
    if (!response.ok) throw new Error("Error fetching distributors");
    return await response.json();
  },

  getDistributorById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/distributors/${id}`);
    if (!response.ok) throw new Error("Error fetching distributor");
    return await response.json();
  },

  createDistributor: async (distributorData) => {
    const response = await fetch(`${API_BASE_URL}/distributors`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(distributorData),
    });
    if (!response.ok) throw new Error("Error creating distributor");
    return await response.json();
  },

  updateDistributor: async (id, distributorData) => {
    const response = await fetch(`${API_BASE_URL}/distributors/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(distributorData),
    });
    if (!response.ok) throw new Error("Error updating distributor");
    return await response.json();
  },

  deleteDistributor: async (id) => {
    const response = await fetch(`${API_BASE_URL}/distributors/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) throw new Error("Error deleting distributor");
    return await response.json();
  },

  getDistributorPlans: async (distributorId) => {
    const response = await fetch(`${API_BASE_URL}/distributors/${distributorId}/plans`);
    if (!response.ok) throw new Error("Error fetching distributor plans");
    return await response.json();
  },

  createDistributorPlan: async (distributorId, planData) => {
    const response = await fetch(`${API_BASE_URL}/distributors/${distributorId}/plans`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(planData),
    });
    if (!response.ok) throw new Error("Error creating distributor plan");
    return await response.json();
  },

  updateDistributorPlan: async (distributorId, planId, planData) => {
    const response = await fetch(`${API_BASE_URL}/distributors/${distributorId}/plans/${planId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(planData),
    });
    if (!response.ok) throw new Error("Error updating distributor plan");
    return await response.json();
  },

  deleteDistributorPlan: async (distributorId, planId) => {
    const response = await fetch(`${API_BASE_URL}/distributors/${distributorId}/plans/${planId}`, {
      method: "DELETE",
    });
    if (!response.ok) throw new Error("Error deleting distributor plan");
    return await response.json();
  },
};

export default distributorService;